import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    createProductFromPackage,
    getProductConditionList,
    getProductStatusList,
    updateMediaForProduct,
} from '../../../store/actions/incoming';
import { searchProductBySku } from '../../../store/actions/inventory.action';
import { getSelectList } from '../../../config/utils';
import {
    allowedExtensions,
    ItemTypeList,
    ReturnProductTypeList,
    videoallowedExtensions,
} from '../../../constant';
import LoaderSpinner from '../../common/Loader';
import AlertDialogModal from '../../common/AlertDialog';
import RenderVideos from './RenderVideos';

const MAX_VIDEO_SIZE_MB = 10;
const MAX_VIDEO_COUNT = 5;
const MAX_IMAGES_PER_ZONE = 5;
const MAX_IMAGE_SIZE_KB = 1024;

const initialItemForm = {
    returnProductType: '',
    sku: '',
    itemName: '',
    inboundProductCondition_Id: 0,
    size: '',
    quantity: 1,
    notes: '',
    inventoryProductId: 0,
};

/**
 * Add Items to a Return Package.
 * Scan a SKU to auto-fetch product details, save line items into an
 * in-modal grid (re-scanning the same SKU increments its quantity),
 * then persist all items on Save.
 */
/* istanbul ignore next */
const AddItemModal = ({ show, handleCancel, packageContext, onSaved }) => {
    const dispatch = useDispatch();
    const UserValue = useSelector((state) => state.auth.user);
    const productConditionList = useSelector(
        (state) => state?.IncomingProductReducer?.productConditionList
    );
    const productStatusList = useSelector(
        (state) => state?.IncomingProductReducer?.productStatusList
    );

    const [itemForm, setItemForm] = useState(initialItemForm);
    const [lineItems, setLineItems] = useState([]);
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
    const [beforeImages, setBeforeImages] = useState([]);
    const [afterImages, setAfterImages] = useState([]);
    const [video, setVideo] = useState([]);

    // Processing timer — auto starts on open, stops on save/close
    const [seconds, setSeconds] = useState(0);
    const [timerRunning, setTimerRunning] = useState(false);
    // seconds already attributed to previously saved line items
    const lastLineSavedSecondsRef = useRef(0);

    const skuInputRef = useRef(null);
    const beforeInputRef = useRef(null);
    const afterInputRef = useRef(null);
    const videoInputRef = useRef(null);

    useEffect(() => {
        if (!show) return;
        if (!productConditionList?.length) dispatch(getProductConditionList());
        if (!productStatusList?.length) dispatch(getProductStatusList());
    }, [show]);

    useEffect(() => {
        if (show) {
            resetAll();
            setTimerRunning(true);
            setTimeout(() => skuInputRef.current?.focus(), 300);
        } else {
            setTimerRunning(false);
        }
    }, [show]);

    useEffect(() => {
        if (!timerRunning) return undefined;
        const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
        return () => clearInterval(interval);
    }, [timerRunning]);

    const resetAll = () => {
        setItemForm(initialItemForm);
        setLineItems([]);
        setErrors({});
        setSeconds(0);
        setShowUnsavedAlert(false);
        lastLineSavedSecondsRef.current = 0;
        clearMedia();
    };

    /**
     * Cancel / ✕ guard — line items live only in memory until "Save Item(s)"
     * persists them, so closing with rows in the grid needs confirmation.
     */
    const handleRequestClose = () => {
        if (loader) return;
        if (lineItems.length > 0) {
            setShowUnsavedAlert(true);
            return;
        }
        handleCancel();
    };

    const handleConfirmDiscard = () => {
        setShowUnsavedAlert(false);
        handleCancel();
    };

    const clearMedia = () => {
        setBeforeImages([]);
        setAfterImages([]);
        setVideo([]);
        if (beforeInputRef.current) beforeInputRef.current.value = '';
        if (afterInputRef.current) afterInputRef.current.value = '';
        if (videoInputRef.current) videoInputRef.current.value = '';
    };

    const formatTime = (totalSeconds) => {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
    };

    const conditionOptions = getSelectList(productConditionList || [], 'id', 'condition');

    const handleFormChange = (name, value) => {
        setItemForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const buildSize = (product) => {
        if (product?.length && product?.width && product?.height) {
            const unit = product.dimensionUnit ? ` ${product.dimensionUnit}` : '';
            return `${product.length}x${product.width}x${product.height}${unit}`;
        }
        return '';
    };

    /**
     * Scan/Enter on SKU field:
     * 1. SKU already in saved list  -> quantity +1 on that line
     * 2. SKU equals current form SKU -> quantity +1 on the form
     * 3. New SKU                    -> fetch product details and auto-fill
     */
    const handleSkuScan = async (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const scanned = String(e.target.value || '').trim();
        if (!scanned) return;

        const existingIndex = lineItems.findIndex(
            (li) => li.sku.toLowerCase() === scanned.toLowerCase()
        );
        if (existingIndex > -1) {
            incrementLineQuantity(existingIndex);
            setItemForm((prev) => ({ ...prev, sku: '' }));
            toast.info(`Quantity increased for ${scanned}`);
            return;
        }

        if (itemForm.sku && itemForm.sku.toLowerCase() === scanned.toLowerCase() && itemForm.itemName) {
            setItemForm((prev) => ({ ...prev, quantity: Number(prev.quantity || 0) + 1 }));
            toast.info(`Quantity increased for ${scanned}`);
            return;
        }

        await fetchProductBySku(scanned);
    };

    const fetchProductBySku = async (scanned) => {
        try {
            setLoader(true);
            const data = {
                pageIndex: 1,
                pageSize: 30,
                sortColumn: '',
                sortOrder: 'DESCENDING',
                searchKeyword: scanned,
                sellerIdList: [],
                companyIdList: [],
            };
            // Promise.resolve keeps Sonar happy: dispatch's static type is not
            // thenable, but redux-thunk returns the service promise at runtime
            const response = await Promise.resolve(dispatch(searchProductBySku(data)));
            const list = response?.data?.Entity?.listInventoryEntity || [];
            const match =
                list.find((p) => String(p.productSku).toLowerCase() === scanned.toLowerCase()) ||
                list[0];
            if (match) {
                setItemForm((prev) => ({
                    ...prev,
                    sku: match.productSku,
                    itemName: match.productName || '',
                    size: buildSize(match),
                    quantity: prev.quantity || 1,
                    inventoryProductId: match.id || 0,
                }));
                setErrors((prev) => ({ ...prev, sku: '', itemName: '' }));
            } else {
                setItemForm((prev) => ({ ...prev, sku: scanned, inventoryProductId: 0 }));
                toast.info('No product found for this SKU — enter details manually');
            }
        } catch (err) {
            console.error('SKU lookup failed:', err);
            toast.error('Unable to fetch product details');
        } finally {
            setLoader(false);
        }
    };

    const incrementLineQuantity = (index, step = 1) => {
        setLineItems((prev) =>
            prev.map((li, i) =>
                i === index ? { ...li, quantity: Math.max(1, Number(li.quantity) + step) } : li
            )
        );
    };

    const handleLineQuantityChange = (index, value) => {
        const quantity = Math.max(1, Number(value) || 1);
        setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, quantity } : li)));
    };

    const handleRemoveLine = (index) => {
        setLineItems((prev) => prev.filter((_, i) => i !== index));
    };

    const validateExtensions = (files, extensions) =>
        files.every((file) =>
            extensions.includes(String(file.name).split('.').pop().toLowerCase())
        );

    const validateImageFile = (file) => {
        const extension = String(file.name).split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(extension)) {
            toast.error('Unsupported File format', 1000);
            return false;
        }
        if (Math.round(file.size / 1024) > MAX_IMAGE_SIZE_KB) {
            toast.info('Each picture size should be less than 1MB', 1000);
            return false;
        }
        return true;
    };

    /** Appends newly selected images to the zone (duplicates skipped, max 5 per zone) */
    const handleImageUpload = (e, existingFiles, setter) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // allow re-selecting the same file later
        if (!files.length) return;

        const merged = [...existingFiles];
        files.filter(validateImageFile).forEach((file) => {
            const isDuplicate = merged.some(
                (f) => f.name === file.name && f.size === file.size
            );
            if (!isDuplicate) merged.push(file);
        });

        if (merged.length > MAX_IMAGES_PER_ZONE) {
            toast.info(`Up to ${MAX_IMAGES_PER_ZONE} images can be uploaded per section`, 1000);
        }
        setter(merged.slice(0, MAX_IMAGES_PER_ZONE));
    };

    const handleRemoveImage = (index, files, setter) => {
        const next = [...files];
        next.splice(index, 1);
        setter(next);
    };

    const handleVideoUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_VIDEO_COUNT) {
            toast.info(`Up to ${MAX_VIDEO_COUNT} videos can be uploaded at one time`, 1000);
            e.target.value = '';
            return;
        }
        if (!validateExtensions(files, videoallowedExtensions)) {
            toast.error('Unsupported File format', 1000);
            e.target.value = '';
            return;
        }
        if (files.some((file) => file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024)) {
            toast.error(`Video size should be less than ${MAX_VIDEO_SIZE_MB}MB`, 1000);
            e.target.value = '';
            return;
        }
        setVideo(files);
    };

    /** Labels of required fields not yet filled — shown next to Save Line Item */
    const getMissingRequiredFields = () => {
        const missing = [];
        if (itemForm.returnProductType === '') missing.push('Return Product Type');
        if (!itemForm.sku?.trim()) missing.push('SKU');
        if (!itemForm.itemName?.trim()) missing.push('Product Name');
        if (!Number(itemForm.inboundProductCondition_Id)) missing.push('Item Condition');
        if (Number(itemForm.quantity) < 1) missing.push('Quantity');
        return missing;
    };

    const validateItemForm = () => {
        const e = {};
        if (itemForm.returnProductType === '') e.returnProductType = 'Return Product Type is required';
        if (!itemForm.sku?.trim()) e.sku = 'SKU is required';
        if (!itemForm.itemName?.trim()) e.itemName = 'Product Name is required';
        if (!Number(itemForm.inboundProductCondition_Id)) e.inboundProductCondition_Id = 'Condition is required';
        if (Number(itemForm.quantity) < 1) e.quantity = 'Quantity must be at least 1';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /** Adds the current form as a line item in the grid (merges quantity for duplicate SKU) */
    const handleSaveLineItem = () => {
        if (!validateItemForm()) {
            toast.error(`Please fill: ${getMissingRequiredFields().join(', ')}`);
            if (!itemForm.sku?.trim()) skuInputRef.current?.focus();
            return;
        }

        const conditionName = conditionOptions.find(
            (c) => Number(c.value) === Number(itemForm.inboundProductCondition_Id)
        )?.label;

        const existingIndex = lineItems.findIndex(
            (li) => li.sku.toLowerCase() === itemForm.sku.trim().toLowerCase()
        );
        if (existingIndex > -1) {
            incrementLineQuantity(existingIndex, Number(itemForm.quantity));
            toast.info(`Quantity updated for ${itemForm.sku.trim()}`);
        } else {
            setLineItems((prev) => [
                ...prev,
                {
                    ...itemForm,
                    sku: itemForm.sku.trim(),
                    quantity: Number(itemForm.quantity),
                    conditionName,
                    beforeImages,
                    afterImages,
                    video,
                    // time spent on this item since the previous line was saved
                    processingDurationSeconds: Math.max(0, seconds - lastLineSavedSecondsRef.current),
                },
            ]);
        }
        lastLineSavedSecondsRef.current = seconds;
        setItemForm((prev) => ({ ...initialItemForm, returnProductType: prev.returnProductType }));
        clearMedia();
        setErrors({});
        skuInputRef.current?.focus();
    };

    const getDefaultStatusId = () =>
        (productStatusList || []).find((s) =>
            /receiv|process|new|open/i.test(s.status || s.statusName || '')
        )?.id ||
        productStatusList?.[0]?.id ||
        0;

    /**
     * Payload for POST api/IncomingProduct/create-from-package.
     * Tracking number, external box id, company seller, product description and
     * seller product id are derived from the package / item on the backend
     * (SP_InboundProduct_CreateFromPackage), which also creates the inventory
     * rows and stamps CreatedSource = WarehouseScan.
     */
    const buildCreatePayload = (li) => ({
        inboundParcelId: Number(packageContext?.internalId) || 0,
        sku: li.sku,
        itemName: li.itemName,
        inboundProductCondition_Id: Number(li.inboundProductCondition_Id),
        inboundProductStatus_Id: getDefaultStatusId(),
        quantity: Math.floor(Number(li.quantity)) || 1,
        size: li.size || '',
        notes: li.notes || '',
        isInventoryType: Number(li.returnProductType) === 1,
        itemType: ItemTypeList.find((t) => t.label === 'Individual')?.value || 2,
        itemPerBundle: 0,
        incomingDate: new Date(),
        processedBy: UserValue?.Value?.employeeId,
        createdBy: UserValue?.Value?.employeeId,
        modifiedBy: UserValue?.Value?.employeeId,
        companyId: Number(packageContext?.companyId) || 0,
        sellerId: Number(packageContext?.sellerId) || 0,
        companySellerId: UserValue?.Value?.companySellerId,
        processingDurationSeconds: li.processingDurationSeconds ?? null,
    });

    const appendFilesToUploadData = (uploadData, files, key) => {
        (files || []).forEach((file) => uploadData.append(key, file));
        return uploadData;
    };

    const uploadLineItemMedia = async (li, newProductId) => {
        if (!li.video?.length && !li.beforeImages?.length && !li.afterImages?.length) return;
        let uploadData = new FormData();
        uploadData.append('id', newProductId);
        uploadData.append('modifiedBy', UserValue?.Value?.employeeId);
        uploadData = appendFilesToUploadData(uploadData, li.video, 'video');
        uploadData = appendFilesToUploadData(uploadData, li.beforeImages, 'beforeProcessingImages');
        uploadData = appendFilesToUploadData(uploadData, li.afterImages, 'afterProcessingImages');
        await updateMediaForProduct(uploadData);
    };

    /** Persists every line item to the database */
    const handleSaveAll = async () => {
        if (!lineItems.length) {
            toast.error('Add at least one line item before saving');
            return;
        }
        if (!getDefaultStatusId()) {
            // status list not loaded yet — sending 0 would fail the FK on insert
            toast.error('Product status list is still loading — please try again');
            dispatch(getProductStatusList());
            return;
        }
        setTimerRunning(false);
        setLoader(true);
        const failedSkus = [];
        try {
            for (const li of lineItems) {
                try {
                    const response = await createProductFromPackage(buildCreatePayload(li));
                    if (response?.data?.Status === 200) {
                        await uploadLineItemMedia(li, response.data.Entity);
                    } else if (!response?.data?._readOnlyBlocked) {
                        failedSkus.push(li.sku);
                    }
                } catch (err) {
                    console.error(`Failed to save item ${li.sku}:`, err);
                    failedSkus.push(li.sku);
                }
            }
            if (failedSkus.length) {
                setLineItems((prev) => prev.filter((li) => failedSkus.includes(li.sku)));
                setTimerRunning(true);
                toast.error(`Failed to save: ${failedSkus.join(', ')}`);
            } else {
                toast.success(`${lineItems.length} item(s) added to package successfully`);
                if (onSaved) onSaved();
                handleCancel();
            }
        } finally {
            setLoader(false);
        }
    };

    const renderSelectedImages = (files, setter, prefix) => (
        <div>
            {files.map((item, index) => (
                <div className="selectedFile" key={`${item.name}-${item.size}`} data-testid={`${prefix}-image-item`}>
                    <div className="fileName">{item.name}</div>
                    <button
                        type="button"
                        className="deleteBtn"
                        aria-label={`Remove ${item.name}`}
                        onClick={() => handleRemoveImage(index, files, setter)}
                        data-testid={`remove-${prefix}-image-${index}`}
                    >
                        <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            ))}
        </div>
    );

    const renderMediaCount = (li) => {
        const count =
            (li.beforeImages?.length || 0) + (li.afterImages?.length || 0) + (li.video?.length || 0);
        return count ? `${count} file(s)` : '-';
    };

    return (
        <>
        <Modal
            show={show}
            onHide={handleRequestClose}
            centered
            backdrop="static"
            size="xl"
            scrollable
            dialogClassName="addItemModalDialog"
        >
            <Modal.Header>
                <div>
                    <Modal.Title>Add Items to Package</Modal.Title>
                    {packageContext?.trackingNumber && (
                        <div className="text-muted small mt-1">
                            Tracking: {packageContext.trackingNumber}
                        </div>
                    )}
                </div>
                <div className="d-flex align-items-center" style={{ gap: 12, marginRight: 30 }}>
                    <div className="text-right">
                        <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>
                            Processing Time
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTime(seconds)}
                        </div>
                    </div>
                    <span
                        className={`badge ${timerRunning ? 'badge-success' : 'badge-danger'}`}
                        data-testid="timer-status"
                    >
                        {timerRunning ? 'Running' : 'Stopped'}
                    </span>
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    className="btn btn-link p-0 border-0"
                    style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, color: '#6c757d' }}
                    onClick={handleRequestClose}
                    data-testid="close-modal-btn"
                >
                    <i className="fa fa-times" />
                </button>
            </Modal.Header>
            <Modal.Body>
                {loader && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}
                <Row>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Return Product Type <span className="require-field">*</span>
                        </div>
                        <Select
                            options={ReturnProductTypeList}
                            value={
                                ReturnProductTypeList.find(
                                    (o) => o.value === Number(itemForm.returnProductType)
                                ) || null
                            }
                            onChange={(opt) =>
                                handleFormChange('returnProductType', opt ? opt.value : '')
                            }
                            placeholder="Select type"
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={180}
                        />
                        {errors.returnProductType && (
                            <span className="error-message">{errors.returnProductType}</span>
                        )}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Scan / Enter SKU <span className="require-field">*</span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Scan barcode or type SKU and press Enter"
                            ref={skuInputRef}
                            value={itemForm.sku}
                            maxLength={100}
                            onChange={(e) => handleFormChange('sku', e.target.value)}
                            onKeyDown={handleSkuScan}
                            data-testid="sku-input"
                        />
                        {errors.sku && <span className="error-message">{errors.sku}</span>}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Product Name <span className="require-field">*</span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Auto-filled after scan"
                            value={itemForm.itemName}
                            maxLength={100}
                            onChange={(e) => handleFormChange('itemName', e.target.value)}
                        />
                        {errors.itemName && <span className="error-message">{errors.itemName}</span>}
                    </Col>
                    <Col xs={12} md={3} className="mb-3">
                        <div className="input-label-txt">
                            Item Condition <span className="require-field">*</span>
                        </div>
                        <Select
                            options={conditionOptions}
                            value={
                                conditionOptions.find(
                                    (o) =>
                                        Number(o.value) ===
                                        Number(itemForm.inboundProductCondition_Id)
                                ) || null
                            }
                            onChange={(opt) =>
                                handleFormChange('inboundProductCondition_Id', opt ? opt.value : 0)
                            }
                            placeholder="Select condition"
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={180}
                        />
                        {errors.inboundProductCondition_Id && (
                            <span className="error-message">{errors.inboundProductCondition_Id}</span>
                        )}
                    </Col>
                    <Col xs={12} md={3} className="mb-3">
                        <div className="input-label-txt">
                            Quantity <span className="require-field">*</span>
                        </div>
                        <div className="d-flex" style={{ gap: 6 }}>
                            <input
                                type="number"
                                min={1}
                                className="form-control"
                                value={itemForm.quantity}
                                onChange={(e) => handleFormChange('quantity', e.target.value)}
                                data-testid="quantity-input"
                            />
                            {itemForm.returnProductType !== '' &&
                                Number(itemForm.returnProductType) === 0 && (
                                    <button
                                        type="button"
                                        className="btn cus-primary-transparent-btn"
                                        title="Scan to fill the SKU field"
                                        onClick={() => skuInputRef.current?.focus()}
                                        data-testid="scan-refocus-btn"
                                    >
                                        <i className="fa fa-barcode" aria-hidden="true"></i> Scan
                                    </button>
                                )}
                        </div>
                        {itemForm.returnProductType !== '' &&
                            Number(itemForm.returnProductType) === 1 && (
                                <span className="text-muted small" data-testid="inventory-scan-hint">
                                    Each barcode scan of this SKU adds 1 unit
                                </span>
                            )}
                        {errors.quantity && <span className="error-message">{errors.quantity}</span>}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">Product Size</div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. 12x8x4 inches"
                            value={itemForm.size}
                            maxLength={100}
                            onChange={(e) => handleFormChange('size', e.target.value)}
                        />
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">Notes / Description</div>
                        <textarea
                            className="form-control"
                            rows={1}
                            placeholder="Describe the product condition, visible damage, labels, etc."
                            value={itemForm.notes}
                            maxLength={100}
                            onChange={(e) => handleFormChange('notes', e.target.value)}
                        />
                    </Col>
                    <Col xs={12} md={4} className="mb-3">
                        <div className="input-label-txt">
                            Before processing image ({beforeImages.length}/{MAX_IMAGES_PER_ZONE})
                        </div>
                        <div>
                            <div className="fileUpload">
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn"
                                    disabled={beforeImages.length >= MAX_IMAGES_PER_ZONE}
                                >
                                    Upload image
                                </button>
                                <input
                                    className="hiddenFileField"
                                    type="file"
                                    id="addItemFileBefore"
                                    multiple
                                    accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
                                    ref={beforeInputRef}
                                    onChange={(e) => handleImageUpload(e, beforeImages, setBeforeImages)}
                                    disabled={beforeImages.length >= MAX_IMAGES_PER_ZONE}
                                    data-testid="before-images-input"
                                />
                            </div>
                            {renderSelectedImages(beforeImages, setBeforeImages, 'before')}
                        </div>
                    </Col>
                    <Col xs={12} md={4} className="mb-3">
                        <div className="input-label-txt">
                            After processing Image ({afterImages.length}/{MAX_IMAGES_PER_ZONE})
                        </div>
                        <div>
                            <div className="fileUpload">
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn"
                                    disabled={afterImages.length >= MAX_IMAGES_PER_ZONE}
                                >
                                    Upload image
                                </button>
                                <input
                                    className="hiddenFileField"
                                    type="file"
                                    id="addItemFileAfter"
                                    multiple
                                    accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
                                    ref={afterInputRef}
                                    onChange={(e) => handleImageUpload(e, afterImages, setAfterImages)}
                                    disabled={afterImages.length >= MAX_IMAGES_PER_ZONE}
                                    data-testid="after-images-input"
                                />
                            </div>
                            {renderSelectedImages(afterImages, setAfterImages, 'after')}
                        </div>
                    </Col>
                    <Col xs={12} md={4} className="mb-3">
                        <div className="input-label-txt">Video (format: mp4, avi, mov)</div>
                        <div>
                            <div className="fileUpload">
                                <button type="button" className="btn cus-seconday-bg-btn">
                                    Upload Video
                                </button>
                                <input
                                    className="hiddenFileField"
                                    type="file"
                                    id="addItemFileVideo"
                                    multiple
                                    accept={videoallowedExtensions.map((ext) => `.${ext}`).join(',')}
                                    ref={videoInputRef}
                                    onChange={handleVideoUpload}
                                    data-testid="video-input"
                                />
                            </div>
                            <RenderVideos
                                video={video}
                                formData={itemForm}
                                setVideo={setVideo}
                                videoInputRef={videoInputRef}
                            />
                        </div>
                    </Col>
                    <Col xs={12} className="mb-3 text-right">
                        {getMissingRequiredFields().length > 0 && (
                            <span
                                className="text-muted small mr-3"
                                style={{ marginRight: 12 }}
                                data-testid="save-line-hint"
                            >
                                <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                                Still required: {getMissingRequiredFields().join(' · ')}
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn cus-seconday-bg-btn"
                            onClick={handleSaveLineItem}
                            disabled={loader}
                            title={
                                getMissingRequiredFields().length
                                    ? `Fill the required fields: ${getMissingRequiredFields().join(', ')}`
                                    : 'Add this item to the list below'
                            }
                            data-testid="save-line-item-btn"
                        >
                            <i className="fa fa-plus mr-1" aria-hidden="true"></i> Save Line Item
                        </button>
                    </Col>
                </Row>

                <div className="table-responsive addItemLineItemsScroll">
                    <Table className="table" data-testid="line-items-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>SKU</th>
                                <th>Product Name</th>
                                <th>Type</th>
                                <th>Condition</th>
                                <th width="90">Qty</th>
                                <th>Media</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length > 0 ? (
                                lineItems.map((li, index) => (
                                    <tr key={li.sku}>
                                        <td>{index + 1}</td>
                                        <td>{li.sku}</td>
                                        <td>{li.itemName}</td>
                                        <td>
                                            {
                                                ReturnProductTypeList.find(
                                                    (o) => o.value === Number(li.returnProductType)
                                                )?.label
                                            }
                                        </td>
                                        <td>{li.conditionName || '-'}</td>
                                        <td>
                                            <input
                                                type="number"
                                                min={1}
                                                className="form-control"
                                                style={{ width: 75 }}
                                                value={li.quantity}
                                                onChange={(e) =>
                                                    handleLineQuantityChange(index, e.target.value)
                                                }
                                                data-testid={`line-qty-${index}`}
                                            />
                                        </td>
                                        <td>{renderMediaCount(li)}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-link text-danger p-0"
                                                onClick={() => handleRemoveLine(index)}
                                                aria-label={`Remove ${li.sku}`}
                                            >
                                                <i className="fa fa-trash" aria-hidden="true"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted">
                                        No items added yet — scan a SKU to begin
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn cus-primary-transparent-btn"
                    onClick={handleRequestClose}
                    data-testid="cancel-btn"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn cus-seconday-bg-btn"
                    onClick={handleSaveAll}
                    disabled={!lineItems.length || loader}
                    data-testid="save-all-btn"
                >
                    Save {lineItems.length > 0 ? `${lineItems.length} ` : ''}Item(s)
                </button>
            </Modal.Footer>
        </Modal>
        <AlertDialogModal
            modalShow={showUnsavedAlert}
            headingText={"UNSAVED ITEMS"}
            messageText={`You have ${lineItems.length} unsaved item(s) in the list that are not saved to the database yet. If you close now, they will be lost. Do you want to close without saving?`}
            handleYesDeleteAlert={handleConfirmDiscard}
            handleCancelDeleteAlert={() => setShowUnsavedAlert(false)}
            modalClass={"modalDeleteWrapper"}
            zIndex={1070}
        />
        </>
    );
};

AddItemModal.propTypes = {
    show: PropTypes.bool,
    handleCancel: PropTypes.func.isRequired,
    packageContext: PropTypes.object,
    onSaved: PropTypes.func,
};

export default AddItemModal;
